import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import ProductPicker from "@/components/ProductPicker";
import {
  ArrowLeft, ArrowRight, Check, ChevronRight, Plus, Trash2, Search,
  Building2, MapPin, Phone, BadgeCheck, FileText, Save, Download,
} from "lucide-react";

const STEPS = [
  { id: 1, label: "Region & Price Type" },
  { id: 2, label: "Customer / Distributor" },
  { id: 3, label: "Products" },
  { id: 4, label: "Preview" },
];

function Stepper({ active }) {
  return (
    <div className="flex items-center gap-0 bg-white rounded-xl border border-slate-200 p-1.5">
      {STEPS.map((s, i) => {
        const done = active > s.id; const current = active === s.id;
        return (
          <React.Fragment key={s.id}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${current ? "bg-slate-900 text-white" : done ? "text-emerald-700" : "text-slate-500"}`}>
              <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-bold ${current ? "bg-white text-slate-900" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                {done ? <Check className="h-3 w-3" /> : s.id}
              </div>
              <div className="text-xs font-medium">{s.label}</div>
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function NewPFI() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);

  // Step 1
  const [regions, setRegions] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [rateType, setRateType] = useState("distributor");

  // Step 2
  const [distributors, setDistributors] = useState([]);
  const [search, setSearch] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterDistrict, setFilterDistrict] = useState("");
  const [distributor, setDistributor] = useState(null);

  // Step 3
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState([]);
  const [cashDisc, setCashDisc] = useState(0);
  const [gstPct, setGstPct] = useState(18);
  const [paymentTerms, setPaymentTerms] = useState("");
  const [remarks, setRemarks] = useState("");
  const [autoFocusIdx, setAutoFocusIdx] = useState(-1);
  const [recentSkus, setRecentSkus] = useState(() => {
    try { return JSON.parse(localStorage.getItem("oms_recent_skus") || "[]"); } catch { return []; }
  });
  const [saving, setSaving] = useState(false);
  const [savedOrder, setSavedOrder] = useState(null);

  useEffect(() => { api.get("/regions").then((r) => setRegions(r.data)); }, []);
  // Sales users are pinned to their assigned region — auto-select + lock.
  // Newly-created users may only have price_region_id (not legacy region_id);
  // use effective_permissions.price_region_id as fallback.
  const salesRegionId = user?.region_id || user?.effective_permissions?.price_region_id || null;
  useEffect(() => {
    if (user?.role === "sales" && salesRegionId && !regionId) setRegionId(salesRegionId);
  }, [user, regionId, salesRegionId]);
  useEffect(() => {
    if (step !== 2) return;
    api.get("/distributors").then((r) => setDistributors(r.data));
  }, [step]);
  useEffect(() => {
    if (step < 3 || !distributor) return;
    const fetchProducts = async () => {
      // STRICT: the distributor MUST have a valid region_id. We NEVER fall back
      // to an unscoped `/products` call — that was leaking cross-region
      // prices into the PFI picker and showing multiple prices per SKU.
      if (!distributor.region_id) {
        setProducts([]);
        toast.error(`${distributor.name} is not mapped to a Region. Open Distributors and set its Region before creating a PFI.`);
        return;
      }
      try {
        const r = await api.get("/products", { params: { region_id: distributor.region_id, rate_type: rateType } });
        // Backend is strict, but add a client-side de-dupe safety net: one row per SKU.
        const seen = new Set();
        const unique = [];
        for (const p of (r.data || [])) {
          if (seen.has(p.sku)) continue;
          seen.add(p.sku); unique.push(p);
        }
        setProducts(unique);
        if ((r.data || []).length === 0) {
          toast.message(`No products priced for ${rateType === "super_stockist" ? "Super Stockist" : "Distributor"} in this region. Upload a price list for this region or change rate type.`);
        }
      } catch { setProducts([]); toast.error("Failed to load products"); }
    };
    fetchProducts();
    setPaymentTerms(distributor.payment_terms || "");
  }, [step, distributor, rateType]);

  // Auto-create first line item on entering Step 3 (Tally-style fast entry)
  useEffect(() => {
    if (step === 3 && items.length === 0) {
      setItems([{ product_id: "", sku: "", name: "", quantity: 1, free_quantity: 0, rate: 0, unit_weight: 0, scheme_text: "", scheme_struct: {} }]);
      setAutoFocusIdx(0);
    }
    // eslint-disable-next-line
  }, [step]);

  // ---- Scheme engine helpers (copied, same behaviour as Orders dialog) ----
  function autoFree(it) { const s = it.scheme_struct || {}; const q = Number(it.quantity || 0); let f = 0; if (s.buy_qty && s.same_free) f += Math.floor(q / Number(s.buy_qty)) * Number(s.same_free); if (s.per_unit_same_free) f += q * Number(s.per_unit_same_free); return f; }
  function effFree(it) { return Number(it.free_quantity || 0) > 0 ? Number(it.free_quantity) : autoFree(it); }
  function recommender(it) { const s = it.scheme_struct || {}; const q = Number(it.quantity || 0); if (s.buy_qty && s.same_free && q > 0 && q % s.buy_qty !== 0) return `Add ${s.buy_qty - (q % s.buy_qty)} more for ${s.same_free} free`; return null; }

  const totals = useMemo(() => {
    const gross = items.reduce((a, i) => a + Number(i.quantity || 0) * Number(i.rate || 0), 0);
    const disc = gross * Number(cashDisc || 0) / 100; const tax = gross - disc;
    const gst = tax * Number(gstPct || 0) / 100; const final = tax + gst;
    const tq = items.reduce((a, i) => a + Number(i.quantity || 0), 0);
    const tf = items.reduce((a, i) => a + effFree(i), 0);
    const tw = items.reduce((a, i) => a + (Number(i.quantity || 0) + effFree(i)) * Number(i.unit_weight || 0), 0);
    return { gross, disc, tax, gst, final, tq, tf, tw };
  }, [items, cashDisc, gstPct]);

  function pushRecent(sku) {
    if (!sku) return;
    setRecentSkus((prev) => {
      const norm = sku.toUpperCase();
      const next = [norm, ...prev.filter((s) => s.toUpperCase() !== norm)].slice(0, 15);
      try { localStorage.setItem("oms_recent_skus", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  function addItem() {
    const nextIdx = items.length;
    setItems([...items, { product_id: "", sku: "", name: "", quantity: 1, free_quantity: 0, rate: 0, unit_weight: 0, scheme_text: "", scheme_struct: {} }]);
    setAutoFocusIdx(nextIdx);
  }
  function updateItem(i, k, v) {
    setItems((prev) => {
      const next = [...prev]; const row = { ...next[i], [k]: v };
      if (k === "product_id") {
        const p = products.find((pr) => pr.id === v);
        if (p) {
          row.sku = p.sku; row.name = p.name; row.category = p.category || "";
          row.rate = rateType === "super_stockist" ? Number(p.super_stockist_price || 0) : Number(p.distributor_price || 0);
          row.unit_weight = Number(p.case_weight_kg || 0) / Math.max(Number(p.pieces_per_case || 1), 1);
          row.unit_weight = Number(p.case_weight_kg || 0); // per-case weight
          row.scheme_text = p.scheme_text || "";
          row.scheme_struct = p.scheme_struct || {};
        }
      }
      next[i] = row; return next;
    });
  }
  function removeItem(i) { setItems((prev) => prev.filter((_, idx) => idx !== i)); }

  function focusQty(i) { setTimeout(() => { const el = document.querySelector(`[data-testid="pfi-qty-${i}"]`); if (el) { el.focus(); el.select?.(); } }, 50); }
  function sameSchemeContext(a, b) { try { return JSON.stringify(a?.scheme_struct || {}) === JSON.stringify(b?.scheme_struct || {}); } catch { return false; } }
  function selectProduct(i, p) {
    const dupIdx = items.findIndex((it, idx) => idx !== i && it.product_id === p.id);
    if (dupIdx !== -1 && sameSchemeContext(items[dupIdx], { scheme_struct: p.scheme_struct })) {
      const incoming = Number(items[i]?.quantity || 0) || 1;
      const snap = items.slice();
      setItems((prev) => { const next = [...prev]; next[dupIdx] = { ...next[dupIdx], quantity: Number(next[dupIdx].quantity || 0) + incoming }; next.splice(i, 1); return next; });
      setAutoFocusIdx(-1);
      const merged = dupIdx > i ? dupIdx - 1 : dupIdx;
      focusQty(merged);
      toast.success(`Merged with existing ${p.sku} (row ${dupIdx + 1}) · +${incoming}`, {
        action: { label: "Keep separate", onClick: () => { setItems(snap); setAutoFocusIdx(-1); focusQty(i); } },
      });
      pushRecent(p.sku);
      return;
    }
    updateItem(i, "product_id", p.id); pushRecent(p.sku); setAutoFocusIdx(-1); focusQty(i);
  }
  function handleQtyChange(i, raw) {
    if (raw === "" || raw === "-") { updateItem(i, "quantity", raw); return; }
    const n = Number(raw); if (Number.isNaN(n)) return;
    if (n < 0) { toast.error("Quantity cannot be negative"); updateItem(i, "quantity", 0); return; }
    updateItem(i, "quantity", Math.floor(n));
  }
  function handleQtyBlur(i) {
    const it = items[i];
    if (it && it.product_id && Number(it.quantity || 0) === 0 && items.length > 1) removeItem(i);
  }
  function handleQtyKeyDown(e, i) {
    if (e.key !== "Enter") return; e.preventDefault();
    const it = items[i]; const q = Number(it.quantity || 0);
    if (!it.product_id) { toast.error("Pick a product first"); return; }
    if (q < 1) { toast.error("Enter quantity (whole cases only)"); return; }
    if (!Number.isInteger(q)) { toast.error("Whole cases only — no decimals"); return; }
    if (i === items.length - 1) addItem(); else focusQty(i + 1);
  }

  // ---- Distributor enhanced search ----
  const states = useMemo(() => Array.from(new Set(distributors.map((d) => d.state).filter(Boolean))).sort(), [distributors]);
  const districts = useMemo(() => Array.from(new Set(distributors.filter((d) => !filterState || d.state === filterState).map((d) => d.district).filter(Boolean))).sort(), [distributors, filterState]);
  const filteredDists = useMemo(() => {
    const q = search.trim().toLowerCase();
    return distributors
      .filter((d) => (!filterState || d.state === filterState) && (!filterDistrict || d.district === filterDistrict))
      .filter((d) => {
        if (!q) return true;
        const hay = [d.name, d.area, d.district, d.state, d.contact_person, d.contact, d.mobile, d.phone, d.gst]
          .filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 200);
  }, [distributors, filterState, filterDistrict, search]);

  // ---- Save / Submit ----
  async function save(submitAfter = false) {
    if (!distributor?.id) return toast.error("Select a distributor");
    const valid = items.filter((i) => i.product_id && Number(i.quantity) > 0);
    if (valid.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        distributor_id: distributor.id, region_id: distributor.region_id, rate_type: rateType,
        payment_terms: paymentTerms, remarks, cash_disc_pct: Number(cashDisc), gst_pct: Number(gstPct),
        items: valid.map((i) => ({ product_id: i.product_id || null, sku: i.sku, name: i.name, category: i.category || "", quantity: Number(i.quantity), free_quantity: Number(i.free_quantity || 0), rate: Number(i.rate), unit_weight: Number(i.unit_weight), scheme_text: i.scheme_text || "", scheme_struct: i.scheme_struct || {} })),
      };
      const { data } = await api.post("/orders", payload);
      setSavedOrder(data);
      if (submitAfter) {
        await api.post(`/orders/${data.id}/action`, { action: "submit", remarks: "" });
        toast.success(`${data.pfi_number} submitted for approval`);
        nav(`/orders/${data.id}`);
      } else {
        toast.success(`${data.pfi_number} saved as draft`);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  }

  async function downloadPDF() {
    if (!savedOrder?.id) return;
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/orders/${savedOrder.id}/pfi.pdf`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob(); const a = document.createElement("a");
    a.href = URL.createObjectURL(b); a.download = `${savedOrder.pfi_number}.pdf`; a.click();
  }

  const canNext = step === 1 ? !!regionId : step === 2 ? !!distributor : step === 3 ? items.some((i) => i.product_id && Number(i.quantity) > 0) : true;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-1"><Button variant="ghost" size="sm" onClick={() => nav("/orders")} className="h-6 px-1.5 text-xs"><ArrowLeft className="h-3 w-3 mr-1" /> Orders</Button></div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">New PFI</h1>
          <p className="text-sm text-slate-500 mt-1">Create a proforma invoice in 4 steps. All values auto-computed.</p>
        </div>
      </div>

      <Stepper active={step} />

      {/* Step 1: Region & Price type */}
      {step === 1 && (
        <Card className="p-6 border border-slate-200 rounded-xl shadow-none max-w-3xl">
          <div className="heading font-semibold text-slate-900 mb-4">Region & Price Type</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Region</Label>
              <Select value={regionId} onValueChange={setRegionId} disabled={user?.role === "sales"}>
                <SelectTrigger data-testid="step1-region"><SelectValue placeholder="Choose region" /></SelectTrigger>
                <SelectContent>{regions.filter((r) => user?.role !== "sales" || r.id === salesRegionId).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">{user?.role === "sales" ? "Locked to your assigned territory." : "Used to filter product master and enforce region-specific pricing."}</div>
            </div>
            <div>
              <Label>Rate Type</Label>
              <Select value={rateType} onValueChange={setRateType}>
                <SelectTrigger data-testid="step1-rate-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="super_stockist">Super Stockist</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Rate type locks the per-case price applied on every line.</div>
            </div>
          </div>
        </Card>
      )}

      {/* Step 2: Distributor */}
      {step === 2 && (
        <Card className="p-6 border border-slate-200 rounded-xl shadow-none">
          <div className="heading font-semibold text-slate-900 mb-4">Pick Distributor</div>
          <div className="flex gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-[320px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by firm, area, district, state, contact person, mobile, or GST"
                className="pl-9" data-testid="dist-search-input" autoFocus
              />
            </div>
            <Select value={filterState || "__all__"} onValueChange={(v) => { setFilterState(v === "__all__" ? "" : v); setFilterDistrict(""); }}>
              <SelectTrigger className="w-44" data-testid="dist-state-filter"><SelectValue placeholder="All states" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All states</SelectItem>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filterDistrict || "__all__"} onValueChange={(v) => setFilterDistrict(v === "__all__" ? "" : v)}>
              <SelectTrigger className="w-44" data-testid="dist-district-filter"><SelectValue placeholder="All districts" /></SelectTrigger>
              <SelectContent><SelectItem value="__all__">All districts</SelectItem>{districts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="text-xs text-slate-500 mb-2">{filteredDists.length} match{filteredDists.length === 1 ? "" : "es"}</div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 max-h-[520px] overflow-y-auto">
            {filteredDists.length === 0 ? <div className="col-span-2 text-sm text-slate-500 p-12 text-center">No distributors match.</div> :
              filteredDists.map((d) => (
                <button
                  key={d.id}
                  onClick={() => setDistributor(d)}
                  data-testid={`dist-card-${d.id}`}
                  className={`text-left p-3 border rounded-lg transition-all ${distributor?.id === d.id ? "border-slate-900 bg-slate-50 ring-1 ring-slate-900" : "border-slate-200 hover:border-slate-400 bg-white"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      {/* AREA is the primary identifier (people remember it more easily than firm name) */}
                      <div className="text-[15px] font-bold text-slate-900 truncate flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-amber-600 shrink-0" />{d.area || d.district || "(no area)"}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 uppercase tracking-wide">{[d.district, d.state].filter(Boolean).join(" · ")}</div>
                      <div className="text-[12px] text-slate-700 mt-1 truncate flex items-center gap-1.5"><Building2 className="h-3 w-3 text-slate-400 shrink-0" />{d.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Phone className="h-3 w-3" /> {d.contact_person || "—"} {d.mobile || d.phone ? ` · ${d.mobile || d.phone}` : ""}</div>
                      {d.gst && <div className="text-[11px] text-slate-500 mono mt-0.5 flex items-center gap-1"><BadgeCheck className="h-3 w-3" /> {d.gst}</div>}
                    </div>
                    {distributor?.id === d.id && <Check className="h-4 w-4 text-emerald-600" />}
                  </div>
                </button>
              ))
            }
          </div>
        </Card>
      )}

      {/* Step 3: Products */}
      {step === 3 && (
        <Card className="p-5 border border-slate-200 rounded-xl shadow-none space-y-4">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="heading text-lg font-bold text-slate-900">{distributor?.area || distributor?.district || "—"}</div>
              <div className="text-xs text-slate-500 mt-0.5">{[distributor?.district, distributor?.state].filter(Boolean).join(" · ")} · Firm: <span className="text-slate-700 font-medium">{distributor?.name}</span> · Rate: <b>{rateType === "super_stockist" ? "Super Stockist" : "Distributor"}</b></div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div><Label className="text-[10px]">Payment Terms</Label><Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} className="h-8" /></div>
              <div><Label className="text-[10px]">Credit Discount %</Label><Input type="number" value={cashDisc} onChange={(e) => setCashDisc(e.target.value)} className="h-8" /></div>
              <div><Label className="text-[10px]">GST %</Label><Input type="number" value={gstPct} onChange={(e) => setGstPct(e.target.value)} className="h-8" /></div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
                <tr><th className="px-2 py-1.5 text-left">Product</th><th className="px-2 py-1.5 w-20 text-right">Qty</th><th className="px-2 py-1.5 w-16 text-right">Free</th><th className="px-2 py-1.5 w-24 text-right">Rate</th><th className="px-2 py-1.5 w-28 text-right">Amount</th><th className="px-2 py-1.5 w-8"></th></tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500 text-sm">No line items yet — click Add Line Item or start typing below.</td></tr>}
                {items.map((it, i) => {
                  const eff = effFree(it); const rec = recommender(it);
                  return (
                    <React.Fragment key={i}>
                      <tr className="border-t border-slate-100">
                        <td className="p-1.5">
                          <ProductPicker products={products} value={it.product_id}
                            rateKey={rateType === "super_stockist" ? "super_stockist_price" : "distributor_price"}
                            onSelect={(p) => selectProduct(i, p)} testId={`pfi-product-${i}`}
                            autoOpen={autoFocusIdx === i} recentSkus={recentSkus} />
                          {it.scheme_text && (
                            <div className="text-[10.5px] mt-0.5 leading-tight flex items-center gap-1.5 flex-wrap">
                              <span className="text-emerald-700/80">{it.scheme_text}</span>
                              {eff > 0 && Number(it.quantity || 0) > 0 && <span className="text-slate-500">→ Applied: <b className="text-slate-900">{Number(it.quantity) + eff}</b> ({Number(it.quantity)}+{eff})</span>}
                            </div>
                          )}
                        </td>
                        <td className="p-1.5"><Input className="h-8 text-right text-[13px] font-semibold text-slate-900 bg-white border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 tabular-nums" type="number" min="0" step="1" value={it.quantity ?? ""} onChange={(e) => handleQtyChange(i, e.target.value)} onBlur={() => handleQtyBlur(i)} onKeyDown={(e) => handleQtyKeyDown(e, i)} data-testid={`pfi-qty-${i}`} /></td>
                        <td className="p-1.5 text-right text-[12.5px]" data-testid={`pfi-free-${i}`}>{eff > 0 ? <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">+{eff}</span> : <span className="text-slate-400">0</span>}</td>
                        <td className="p-1.5 text-right text-slate-700 text-[12.5px]" data-testid={`pfi-rate-${i}`}>{it.rate ? formatINR(it.rate) : <span className="text-slate-400">—</span>}</td>
                        <td className="p-1.5 text-right font-medium text-[12.5px]" data-testid={`pfi-amount-${i}`}>{formatINR(Number(it.quantity || 0) * Number(it.rate || 0))}</td>
                        <td className="p-1.5"><Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5 text-slate-500" /></Button></td>
                      </tr>
                      {rec && <tr className="bg-amber-50"><td colSpan={6} className="px-3 py-1 text-xs text-amber-800">{rec}</td></tr>}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
            <div className="p-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="h-3 w-3 mr-1" /> Add Line Item <kbd className="ml-2 text-[10px] text-slate-500 font-mono bg-white border border-slate-200 px-1 py-[1px] rounded">Enter</kbd></Button>
              <div className="flex gap-4 text-xs text-slate-600 flex-wrap items-center">
                <div>Qty <b className="text-slate-900">{totals.tq}</b></div>
                <div>Free <b className="text-emerald-700">+{totals.tf.toFixed(0)}</b></div>
                <div>Wt <b className="text-slate-900">{totals.tw.toFixed(2)} kg</b></div>
                <div className="h-3 w-px bg-slate-300" />
                <div>Gross <b className="text-slate-900">{formatINR(totals.gross)}</b></div>
                <div>Credit Disc <b className="text-slate-900">-{formatINR(totals.disc)}</b></div>
                <div>Taxable <b className="text-slate-900">{formatINR(totals.tax)}</b></div>
                <div>GST <b className="text-slate-900">{formatINR(totals.gst)}</b></div>
                <div className="text-sm">Final <b className="text-slate-900">{formatINR(totals.final)}</b></div>
              </div>
            </div>
          </div>

          <div>
            <Label>Remarks (optional)</Label>
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Any special instructions…" />
          </div>
        </Card>
      )}

      {/* Step 4: Preview */}
      {step === 4 && (
        <Card className="p-6 border border-slate-200 rounded-xl shadow-none space-y-5">
          <div className="flex items-start justify-between pb-4 border-b border-slate-200">
            <div>
              <div className="text-xs uppercase tracking-wide text-slate-500">Proforma Invoice</div>
              <div className="heading text-2xl font-bold text-slate-900 mt-0.5">{savedOrder?.pfi_number || "DRAFT · not saved"}</div>
              <div className="text-xs text-slate-500 mt-0.5">Rate: <b className="text-slate-700">{rateType === "super_stockist" ? "Super Stockist" : "Distributor"}</b> · GST: <b>{gstPct}%</b> · Credit Disc: <b>{cashDisc || 0}%</b></div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wide text-slate-500">Billed To</div>
              <div className="text-base font-bold text-slate-900 mt-0.5">{distributor?.area || distributor?.district || "—"}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500">{[distributor?.district, distributor?.state].filter(Boolean).join(" · ")}</div>
              <div className="text-[13px] text-slate-700 mt-0.5">{distributor?.name}</div>
              <div className="text-xs text-slate-500">{distributor?.contact_person} {distributor?.mobile ? `· ${distributor?.mobile}` : ""}</div>
              {distributor?.gst && <div className="text-[11px] text-slate-500 mono">{distributor.gst}</div>}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
              <tr><th className="p-2 text-left">SKU</th><th className="p-2 text-left">Product</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Free</th><th className="p-2 text-right">Rate</th><th className="p-2 text-right">Amount</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.filter((i) => i.product_id).map((it, i) => (
                <tr key={i}>
                  <td className="p-2 mono text-[11px] font-semibold">{it.sku}</td>
                  <td className="p-2">{it.name}{it.scheme_text && <div className="text-[10px] text-emerald-700">🎁 {it.scheme_text}</div>}</td>
                  <td className="p-2 text-right">{it.quantity}</td>
                  <td className="p-2 text-right text-emerald-700">{effFree(it) || ""}</td>
                  <td className="p-2 text-right">{formatINR(it.rate)}</td>
                  <td className="p-2 text-right font-medium">{formatINR(Number(it.quantity) * Number(it.rate))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between"><span>Gross</span><span>{formatINR(totals.gross)}</span></div>
              <div className="flex justify-between"><span>Credit Discount ({cashDisc || 0}%)</span><span>-{formatINR(totals.disc)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1"><span>Taxable</span><span>{formatINR(totals.tax)}</span></div>
              <div className="flex justify-between"><span>GST ({gstPct}%)</span><span>{formatINR(totals.gst)}</span></div>
              <div className="flex justify-between text-base font-bold pt-2 border-t-2 border-slate-900"><span>Final</span><span>{formatINR(totals.final)}</span></div>
            </div>
          </div>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between flex-wrap gap-3 sticky bottom-0 bg-slate-50/95 backdrop-blur border-t border-slate-200 -mx-8 px-8 py-3 z-10">
        <Button variant="outline" onClick={() => { if (step === 1) nav("/orders"); else setStep(step - 1); }} data-testid="wiz-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> {step === 1 ? "Exit" : "Back"}
        </Button>
        <div className="flex gap-2">
          {step === 3 && <Button variant="outline" onClick={() => save(false)} disabled={saving || !canNext} data-testid="wiz-save-draft"><Save className="h-4 w-4 mr-1.5" /> Save Draft</Button>}
          {step < 4 && <Button onClick={() => setStep(step + 1)} disabled={!canNext} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wiz-next">Continue <ArrowRight className="h-4 w-4 ml-1" /></Button>}
          {step === 4 && !savedOrder && <Button onClick={() => save(false)} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="wiz-save-final"><Save className="h-4 w-4 mr-1.5" /> Save Draft</Button>}
          {step === 4 && savedOrder && <Button variant="outline" onClick={downloadPDF} data-testid="wiz-pdf"><Download className="h-4 w-4 mr-1.5" /> Download PDF</Button>}
          {step === 4 && <Button onClick={() => save(true)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="wiz-submit"><FileText className="h-4 w-4 mr-1.5" /> {savedOrder ? "Submit for Approval" : "Save & Submit"}</Button>}
        </div>
      </div>
    </div>
  );
}
